import React, { useState } from "react";

import { CheckCircle, Mail } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { useToast } from "~/components/ui/use-toast";
import { supabase } from "~/integrations/supabase/client";

interface ForgotPasswordDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ForgotPasswordDialog: React.FC<ForgotPasswordDialogProps> = ({
  isOpen,
  onOpenChange,
}) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      toast({
        title: "Error",
        description: "Por favor ingresa tu correo electrónico",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Use our custom edge function for professional email template
      const { data, error } = await supabase.functions.invoke(
        "send-password-reset-email",
        {
          body: {
            email: email.trim(),
            redirectUrl: `${window.location.origin}/reset-password`,
          },
        }
      );

      if (error) {
        console.error("Password reset error:", error);

        // Handle different error types
        if (error.message?.includes("not found")) {
          toast({
            title: "Error",
            description:
              "No se encontró una cuenta con este correo electrónico",
            variant: "destructive",
          });
        } else if (error.message?.includes("Email service not configured")) {
          toast({
            title: "Servicio no disponible",
            description:
              "El servicio de correo no está configurado. Contacta al administrador.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Error",
            description:
              "Hubo un problema al enviar el correo. Inténtalo de nuevo.",
            variant: "destructive",
          });
        }
        return;
      }

      // Success response from edge function
      if (data?.success) {
        setEmailSent(true);
        toast({
          title: "Correo enviado",
          description:
            "Se ha enviado un enlace de recuperación a tu correo electrónico",
        });
      } else {
        // Fallback message (edge function returns success even if user doesn't exist for security)
        setEmailSent(true);
        toast({
          title: "Solicitud procesada",
          description:
            "Si existe una cuenta con este correo, recibirás un enlace de recuperación",
        });
      }
    } catch (error) {
      console.error("Unexpected error:", error);

      // Fallback to default Supabase method if edge function fails
      console.log("Falling back to default Supabase method...");

      try {
        const { error: fallbackError } =
          await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/reset-password`,
          });

        if (fallbackError) {
          toast({
            title: "Error",
            description:
              fallbackError.message === "User not found"
                ? "No se encontró una cuenta con este correo electrónico"
                : "Hubo un problema al enviar el correo. Inténtalo de nuevo.",
            variant: "destructive",
          });
          return;
        }

        setEmailSent(true);
        toast({
          title: "Correo enviado",
          description:
            "Se ha enviado un enlace de recuperación a tu correo electrónico",
        });
      } catch (fallbackError) {
        console.error("Fallback also failed:", fallbackError);
        toast({
          title: "Error",
          description:
            "Hubo un problema inesperado. Inténtalo de nuevo más tarde.",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      setEmail("");
      setEmailSent(false);
      setIsLoading(false);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {emailSent ? (
              <>
                <CheckCircle className="h-5 w-5 text-green-500" />
                Correo Enviado
              </>
            ) : (
              <>
                <Mail className="h-5 w-5" />
                Recuperar Contraseña
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {emailSent
              ? "Te hemos enviado un correo que incluye un enlace de recuperación. Revisa tu bandeja de entrada y sigue las instrucciones para restablecer tu contraseña."
              : "Ingresa tu correo electrónico y te enviaremos un enlace profesional para restablecer tu contraseña."}
          </DialogDescription>
        </DialogHeader>

        {!emailSent ? (
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">Correo Electrónico</Label>
                <Input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu-correo@empresa.com"
                  required
                  disabled={isLoading}
                  className="w-full"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleClose(false)}
                disabled={isLoading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Enviando..." : "Enviar Enlace"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <DialogFooter>
            <Button onClick={() => handleClose(false)} className="w-full">
              Entendido
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
